import { pushToElevarDataLayer } from '..'
import { observeIntersection, waitForElement } from '../../utilities/observer'
import { transformToElevarProduct } from '../utils/elevar-product-mapper'
import type { ProductCardData } from 'types'

let items: ProductCardData[] = []
const trackedProductIds = new Set<string>()
const BATCH_SIZE = window.innerWidth >= 1024 ? 4 : 2
const THROTTLE_DELAY = 2000
let lastFireTime = 0
let pendingTimeout: NodeJS.Timeout | null = null

const fireTrackingEvent = (): void => {
  if (items.length === 0) return

  const elevarItems = items.map(transformToElevarProduct)

  pushToElevarDataLayer('dl_view_item_list', {
    ecommerce: {
      currencyCode: 'USD',
      impressions: elevarItems,
    },
  })

  items = []
  lastFireTime = Date.now()
}

const scheduleTracking = (): void => {
  const now = Date.now()
  const timeSinceLastFire = now - lastFireTime

  if (pendingTimeout) {
    clearTimeout(pendingTimeout)
    pendingTimeout = null
  }

  if (items.length >= BATCH_SIZE) {
    if (timeSinceLastFire >= THROTTLE_DELAY) {
      fireTrackingEvent()
    } else {
      const remainingTime = THROTTLE_DELAY - timeSinceLastFire

      pendingTimeout = setTimeout(fireTrackingEvent, remainingTime)
    }
    return
  }

  if (timeSinceLastFire >= THROTTLE_DELAY) {
    pendingTimeout = setTimeout(fireTrackingEvent, 1000)
  } else {
    const remainingTime = THROTTLE_DELAY - timeSinceLastFire + 1000

    pendingTimeout = setTimeout(fireTrackingEvent, remainingTime)
  }
}

const dataLayerListEvent = (productCard: Element): void => {
  const rect = productCard.getBoundingClientRect()
  const isActuallyVisible = rect.top < window.innerHeight && rect.bottom > 0

  if (!isActuallyVisible) return

  const scriptElement = productCard.querySelector(
    'script[type="application/json"]'
  ) as HTMLScriptElement

  if (!scriptElement?.textContent) return

  try {
    const data: ProductCardData = JSON.parse(scriptElement.textContent)

    if (trackedProductIds.has(data.item_variant)) return

    trackedProductIds.add(data.item_variant)
    items.push(data)
    scheduleTracking()
  } catch (error) {
    console.error('Error parsing product card data:', error)
  }
}

const observeProducts = (): void => {
  waitForElement('[data-element="product-grid"]', () => {
    observeIntersection(
      '[data-component="product-item"]',
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.3,
      },
      (entry: Element) => dataLayerListEvent(entry),
      true
    )
  })
}

const observeLoadMore = (): void => {
  waitForElement('.load-more-observer', () => {
    let index = Pura?.collection?.shopify?.includes(Pura.collection.handle)
      ? 1
      : 0

    observeIntersection(
      '.load-more-observer',
      { root: null, rootMargin: '0px', threshold: 0.5 },
      () => {
        if (index > 0) {
          index++
          observeProducts()
        } else {
          index++
        }
      },
      true
    )
  })
}

const cleanup = (): void => {
  if (pendingTimeout) {
    clearTimeout(pendingTimeout)
    pendingTimeout = null
  }
  if (items.length > 0) {
    fireTrackingEvent()
  }
}

if (Pura?.template.name === 'collection' || Pura?.template.name === 'search') {
  window.addEventListener('beforeunload', cleanup)

  observeProducts()
  observeLoadMore()
}

export { observeProducts }
